/*
 * FAQアコーディオン（<details>ネイティブ + Web Animations APIで高さを実測アニメーション）。
 * 2026-09-03: れいやとのブレスト結果を反映。
 * - CSSのgrid-template-rowsトリックは「開くときだけ動き、2回目以降は動かない」という
 *   非対称な劣化が起きたため廃止し、毎回scrollHeightを実測してJSでアニメーションする方式に統一。
 * - JS失敗時・prefers-reduced-motion時はネイティブ<details>として即時開閉する（機能劣化のみ）。
 */
if (!customElements.get('faq-accordion')) {
  class FaqAccordion extends HTMLElement {
    constructor() {
      super();
      this.states = new WeakMap();
      this.onClick = this.onClick.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      // 初期状態で閉じている回答は、最初からフォーカス・スクリーンリーダー対象外にする。
      this.querySelectorAll('.faq-category__item').forEach((details) => {
        const wrapper = details.querySelector(':scope > .faq-category__answer-wrapper');
        this.setInert(wrapper, !details.open);
      });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    onClick(event) {
      const summary = event.target.closest('summary');
      if (!summary || !this.contains(summary)) return;

      const details = summary.parentElement;
      if (!details || !details.matches('.faq-category__item')) return;

      // Web Animations API / matchMedia非対応環境ではpreventDefaultせず、
      // ネイティブ<details>の開閉に任せる（機能劣化のみで開閉自体は必ず動く）。
      if (typeof window.matchMedia !== 'function' || typeof details.animate !== 'function') {
        return;
      }

      event.preventDefault();

      const state = this.getState(details);
      // アニメーション中なら現在の目標状態を反転。停止中なら現在のopen状態を反転。
      const shouldOpen = state.animation ? !state.targetOpen : !details.open;

      try {
        this.animate(details, shouldOpen, state);
      } catch (error) {
        // アニメーション処理中に予期せぬ例外が起きても、開閉自体は保証する。
        state.animation && state.animation.cancel();
        state.animation = null;
        state.targetOpen = shouldOpen;
        this.cleanup(details);
        details.open = shouldOpen;
      }
    }

    getState(details) {
      let state = this.states.get(details);
      if (!state) {
        state = { animation: null, targetOpen: details.open };
        this.states.set(details, state);
      }
      return state;
    }

    animate(details, shouldOpen, state) {
      const wrapper = details.querySelector(':scope > .faq-category__answer-wrapper');
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (reduceMotion) {
        state.animation && state.animation.cancel();
        state.animation = null;
        state.targetOpen = shouldOpen;
        details.open = shouldOpen;
        this.setInert(wrapper, !shouldOpen);
        this.cleanup(details);
        return;
      }

      // 反転操作時、画面上で実際に見えている高さ。
      const startHeight = details.getBoundingClientRect().height;

      const previousAnimation = state.animation;
      state.animation = null;
      previousAnimation && previousAnimation.cancel();

      state.targetOpen = shouldOpen;

      // 開く場合は内容を測定できるよう先にopenを付ける。閉じる場合は完了までopenを残す。
      if (shouldOpen) {
        details.open = true;
        this.setInert(wrapper, false);
      } else {
        // 閉じ始めた時点で回答内のリンク等を先にフォーカス・スクリーンリーダー対象から外す
        // （details.openはアニメーション完了まで残るが、見えなくなっていく操作要素には
        //   このタイミングで到達できないようにする）。
        this.setInert(wrapper, true);
      }

      const styles = getComputedStyle(details);
      const borderHeight = parseFloat(styles.borderTopWidth) + parseFloat(styles.borderBottomWidth);
      const summary = details.querySelector(':scope > summary');
      const endHeight = shouldOpen
        ? details.scrollHeight + borderHeight
        : summary.getBoundingClientRect().height + borderHeight;

      details.style.overflow = 'hidden';

      const animation = details.animate(
        { height: [`${startHeight}px`, `${endHeight}px`] },
        { duration: 350, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
      );

      state.animation = animation;

      animation.onfinish = () => {
        if (state.animation !== animation) return;
        details.open = shouldOpen;
        state.animation = null;
        this.cleanup(details);
      };

      animation.oncancel = () => {
        // 新しい反転アニメーションが既に始まっている場合は、そのスタイルを消さない。
        if (state.animation === animation) {
          state.animation = null;
          this.cleanup(details);
        }
      };
    }

    cleanup(details) {
      details.style.removeProperty('height');
      details.style.removeProperty('overflow');
    }

    setInert(wrapper, isInert) {
      if (!wrapper) return;
      if (isInert) {
        wrapper.setAttribute('inert', '');
      } else {
        wrapper.removeAttribute('inert');
      }
    }
  }

  customElements.define('faq-accordion', FaqAccordion);
}
